import { fetchAPI, fetchBlobWithSession, API_BASE_URL } from '@/lib/api';

// --- Types ---

export type IssueReportStatus = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
export type IssueReportPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
/** 어느 화면에서 온 신고인가. 신고자 신원과는 별개다. */
export type IssueReportSource = 'DASHBOARD' | 'AI_EDITOR_MEMBER' | 'GUEST_APPLY';

/**
 * 신고 API 를 무엇으로 부르는가. `applicant-link` 는 익명이나 계정 종류가 아니다 —
 * 로그인 세션 대신 **작성 링크(Poll token)** 로 인증한다는 뜻이다. 신고자 신원은
 * 서버가 공유의 수신자 계정으로 확정한다.
 * 계획: docs/plans/2026-08-24/guest-apply-임시계정-문제신고-연결-계획.md §9.1
 */
export type IssueReportAuth =
  | { kind: 'member'; accessToken: string }
  | { kind: 'applicant-link'; accessId: string; pollToken: string };

export interface IssueReport {
  id: string;
  reporterKeycloakId: string;
  reporterEmail: string;
  reporterName: string;
  description: string;
  hasScreenshots: boolean;
  screenshotCount: number;
  /** 화면·사진 밖의 첨부 문서(관리자 상세에만 온다). */
  files?: { name: string; size: number }[];
  diagnosticData: string | null;
  status: IssueReportStatus;
  priority: IssueReportPriority;
  currentUrl: string;
  userAgent: string;
  screenSize: string;
  adminNote: string | null;
  sourceType?: IssueReportSource;
  sourceTaskId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IssueComment {
  id: string;
  authorKeycloakId: string;
  authorName: string;
  authorEmail: string;
  isAdmin: boolean;
  content: string;
  createdAt: string;
}

export interface IssueReportsResponse {
  success: boolean;
  data: {
    content: IssueReport[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  };
}

// --- User API ---

/** 서버가 내리는 거절. `code` 는 개인정보 없는 고정 식별자다. */
export class IssueReportRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
  ) {
    super(message);
    this.name = 'IssueReportRequestError';
  }
}

async function postIssueReport(url: string, authorization: string, formData: FormData) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { Authorization: authorization },
    body: formData,
  });

  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as { message?: string; code?: string };
    throw new IssueReportRequestError(
      err.message || '이슈 신고에 실패했습니다.',
      response.status,
      err.code ?? null,
    );
  }

  return response.json() as Promise<{ success: boolean; message: string; data?: { id: string } }>;
}

/** 로그인 회원 — Dashboard 사이드바와 `/ai-editor`. */
export function createMemberIssueReport(accessToken: string, formData: FormData) {
  return postIssueReport(`${API_BASE_URL}/api/issue-reports`, `Bearer ${accessToken}`, formData);
}

/**
 * 로그인 세션 없는 신청자 — `/guest-apply/{accessId}`.
 * 이름·이메일·taskId 는 보내지 않는다. 서버가 token 과 공유로 확정한다.
 */
export function createApplicantIssueReport(accessId: string, pollToken: string, formData: FormData) {
  return postIssueReport(
    `${API_BASE_URL}/api/guest/access/${encodeURIComponent(accessId)}/issue-reports`,
    `Poll ${pollToken}`,
    formData,
  );
}

export function createIssueReport(auth: IssueReportAuth, formData: FormData) {
  return auth.kind === 'member'
    ? createMemberIssueReport(auth.accessToken, formData)
    : createApplicantIssueReport(auth.accessId, auth.pollToken, formData);
}

// --- User: 내 이슈 조회 ---

export async function getMyIssueReports(
  token: string,
  params?: { page?: number; size?: number },
): Promise<IssueReportsResponse> {
  const searchParams = new URLSearchParams();
  if (params?.page != null) searchParams.set('page', String(params.page));
  if (params?.size != null) searchParams.set('size', String(params.size));
  const query = searchParams.toString();
  return fetchAPI(`/api/issue-reports/my${query ? `?${query}` : ''}`, { token });
}

export async function getMyIssueComments(
  token: string,
  reportId: string,
): Promise<{ success: boolean; data: IssueComment[] }> {
  return fetchAPI(`/api/issue-reports/${reportId}/comments`, { token });
}

export async function addMyIssueComment(
  token: string,
  reportId: string,
  content: string,
): Promise<{ success: boolean; data: IssueComment }> {
  return fetchAPI(`/api/issue-reports/${reportId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ content }),
    token,
  });
}

// --- Admin API ---

export async function getAdminIssueReports(
  token: string,
  params?: { q?: string; status?: string; page?: number; size?: number },
): Promise<IssueReportsResponse> {
  const searchParams = new URLSearchParams();
  if (params?.q) searchParams.set('q', params.q);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.page != null) searchParams.set('page', String(params.page));
  if (params?.size != null) searchParams.set('size', String(params.size));
  const query = searchParams.toString();
  return fetchAPI(`/api/admin/issue-reports${query ? `?${query}` : ''}`, { token });
}

export async function getAdminIssueReport(
  token: string,
  id: string,
): Promise<{ success: boolean; data: IssueReport }> {
  return fetchAPI(`/api/admin/issue-reports/${id}`, { token });
}

export async function updateAdminIssueReport(
  token: string,
  id: string,
  data: { status?: IssueReportStatus; priority?: IssueReportPriority; adminNote?: string },
): Promise<{ success: boolean; message: string }> {
  return fetchAPI(`/api/admin/issue-reports/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
    token,
  });
}

export async function getAdminIssueComments(
  token: string,
  reportId: string,
): Promise<{ success: boolean; data: IssueComment[] }> {
  return fetchAPI(`/api/admin/issue-reports/${reportId}/comments`, { token });
}

export async function addAdminIssueComment(
  token: string,
  reportId: string,
  content: string,
): Promise<{ success: boolean; data: IssueComment }> {
  return fetchAPI(`/api/admin/issue-reports/${reportId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ content }),
    token,
  });
}

/** 첨부 문서 한 건을 원래 이름으로 내려받는다. 브라우저에서 열지 않는다. */
export async function downloadIssueFile(reportId: string, index: number, name: string): Promise<void> {
  const response = await fetchBlobWithSession(`/api/admin/issue-reports/${reportId}/files/${index}`);
  if (!response.ok) throw new Error('첨부 파일을 받지 못했습니다');
  const url = URL.createObjectURL(await response.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function getIssueScreenshot(
  reportId: string,
  index: number,
): Promise<string> {
  const response = await fetchBlobWithSession(`/api/admin/issue-reports/${reportId}/screenshots/${index}`);
  if (!response.ok) throw new Error('스크린샷 로드 실패');
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}
